import moment from "moment";
import type { FloorHeatMapRow, HeatMapDayCount, OfficeHeatMap } from "@features/reporting/services/OfficeHeatMapService";

export type HalfDayMode = 'FULL' | 'AM' | 'PM';
export type TypicalWeekStat = 'AVERAGE' | 'PEAK';

// Longer ranges than this show one column per week instead of per day
export const MAX_DAY_COLUMNS = 62;

export interface HeatColumn {
  key: string;
  heading: string;
  subheading: string;
  // Starts a new group (a new weekday in the typical week), drawn with a divider
  startsGroup: boolean;
}

export interface HeatCell {
  // people / capacity; undefined when the floor has no capacity
  ratio?: number;
  people: number;
  description: string;
  // For the tooltip, under the floor name
  when: string;
  lines: string[];
}

export interface HeatGridRow {
  floorId: number;
  label: string;
  capacity: number;
  hasFloorPlan: boolean;
  cells: HeatCell[];
}

export interface HeatGrid {
  columns: HeatColumn[];
  rows: HeatGridRow[];
}

function weekdayName(isoWeekday: number): string {
  return moment().isoWeekday(isoWeekday).format('dddd');
}

function countsByDate(row: FloorHeatMapRow): Map<string, HeatMapDayCount> {
  return new Map(row.days.map(d => [d.date, d]));
}

function halfDayValue(day: HeatMapDayCount | undefined, mode: HalfDayMode): number {
  if (!day) return 0;
  if (mode === 'AM') return day.am;
  if (mode === 'PM') return day.pm;
  return (day.am + day.pm) / 2;
}

function formatPeople(people: number): string {
  return Number.isInteger(people) ? `${people}` : people.toFixed(1);
}

function cell(row: FloorHeatMapRow, people: number, when: string): HeatCell {
  const ratio = row.capacity > 0 ? people / row.capacity : undefined;
  const percent = ratio === undefined ? 'no capacity set' : `${Math.round(ratio * 100)}%`;
  return {
    ratio,
    people,
    description: `${row.label}, ${when}: ${formatPeople(people)} of ${row.capacity} (${percent})`,
    when: when.charAt(0).toUpperCase() + when.slice(1),
    lines: [
      ratio === undefined ? 'No capacity set' : `${percent} of capacity`,
      `${formatPeople(people)} ${people === 1 ? 'person' : 'people'} in, capacity ${row.capacity}`,
    ],
  };
}

function mean(values: number[]): number {
  return values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length;
}

const MODE_SUFFIX: Record<HalfDayMode, string> = { FULL: '', AM: ', morning', PM: ', afternoon' };

export function buildDateGrid(report: OfficeHeatMap, mode: HalfDayMode): HeatGrid {
  if (report.dates.length <= MAX_DAY_COLUMNS) {
    const columns = report.dates.map((date, index) => {
      const day = moment(date);
      return {
        key: date,
        heading: day.format('ddd'),
        subheading: day.format('D MMM'),
        startsGroup: index > 0 && day.isoWeekday() < moment(report.dates[index - 1]).isoWeekday(),
      };
    });
    const rows = report.floors.map(floor => {
      const byDate = countsByDate(floor);
      return {
        ...rowBase(floor),
        cells: report.dates.map(date => cell(floor, halfDayValue(byDate.get(date), mode),
          `${moment(date).format('ddd D MMM YYYY')}${MODE_SUFFIX[mode]}`)),
      };
    });
    return { columns, rows };
  }

  // One column per week, the daily average across that week's working dates
  const weeks = new Map<string, string[]>();
  report.dates.forEach(date => {
    const weekStart = moment(date).startOf('isoWeek').format('YYYY-MM-DD');
    weeks.set(weekStart, [...(weeks.get(weekStart) ?? []), date]);
  });
  const columns = Array.from(weeks.keys()).map(weekStart => ({
    key: weekStart,
    heading: 'w/c',
    subheading: moment(weekStart).format('D MMM'),
    startsGroup: false,
  }));
  const rows = report.floors.map(floor => {
    const byDate = countsByDate(floor);
    return {
      ...rowBase(floor),
      cells: Array.from(weeks.entries()).map(([weekStart, dates]) => cell(floor,
        mean(dates.map(date => halfDayValue(byDate.get(date), mode))),
        `week of ${moment(weekStart).format('D MMM YYYY')}${MODE_SUFFIX[mode]}, daily average`)),
    };
  });
  return { columns, rows };
}

export function buildTypicalWeekGrid(report: OfficeHeatMap, stat: TypicalWeekStat): HeatGrid {
  const datesByWeekday = new Map<number, string[]>();
  report.dates.forEach(date => {
    const weekday = moment(date).isoWeekday();
    datesByWeekday.set(weekday, [...(datesByWeekday.get(weekday) ?? []), date]);
  });
  const weekdays = Array.from(datesByWeekday.keys()).sort((a, b) => a - b);

  const columns: HeatColumn[] = weekdays.flatMap((weekday, index) => (['AM', 'PM'] as const).map(period => ({
    key: `${weekday}-${period}`,
    heading: period === 'AM' ? moment().isoWeekday(weekday).format('ddd') : '',
    subheading: period,
    startsGroup: period === 'AM' && index > 0,
  })));

  const rows = report.floors.map(floor => {
    const byDate = countsByDate(floor);
    return {
      ...rowBase(floor),
      cells: weekdays.flatMap(weekday => (['AM', 'PM'] as const).map(period => {
        const values = datesByWeekday.get(weekday)!.map(date => halfDayValue(byDate.get(date), period));
        const people = stat === 'PEAK' ? Math.max(...values) : mean(values);
        const when = `${stat === 'PEAK' ? 'busiest' : 'average'} ${weekdayName(weekday)} ${period === 'AM' ? 'morning' : 'afternoon'}`;
        return cell(floor, people, when);
      })),
    };
  });
  return { columns, rows };
}

function rowBase(floor: FloorHeatMapRow) {
  return { floorId: floor.floorId, label: floor.label, capacity: floor.capacity, hasFloorPlan: floor.hasFloorPlan };
}

export interface HeatMapSummary {
  // Whole office, full days, as a share of total floor capacity
  averageOccupancy?: number;
  busiestWeekday?: { name: string; ratio: number };
  quietestWeekday?: { name: string; ratio: number };
  peak?: { floorLabel: string; date: string; period: 'AM' | 'PM'; people: number; capacity: number; ratio: number };
}

export function summarise(report: OfficeHeatMap): HeatMapSummary {
  const totalCapacity = report.floors.reduce((sum, f) => sum + f.capacity, 0);
  if (totalCapacity === 0 || report.dates.length === 0) {
    return {};
  }

  const peopleDaysByDate = new Map<string, number>();
  report.floors.forEach(floor => floor.days.forEach(day => {
    peopleDaysByDate.set(day.date, (peopleDaysByDate.get(day.date) ?? 0) + (day.am + day.pm) / 2);
  }));
  const totalPeopleDays = Array.from(peopleDaysByDate.values()).reduce((a, b) => a + b, 0);

  const weekdayRatios = new Map<number, number[]>();
  report.dates.forEach(date => {
    const weekday = moment(date).isoWeekday();
    const ratio = (peopleDaysByDate.get(date) ?? 0) / totalCapacity;
    weekdayRatios.set(weekday, [...(weekdayRatios.get(weekday) ?? []), ratio]);
  });
  const weekdays = Array.from(weekdayRatios.entries())
    .map(([weekday, ratios]) => ({ name: weekdayName(weekday), ratio: mean(ratios) }));
  const byRatio = [...weekdays].sort((a, b) => b.ratio - a.ratio);

  let peak: HeatMapSummary['peak'];
  report.floors.filter(f => f.capacity > 0).forEach(floor => floor.days.forEach(day => {
    (['AM', 'PM'] as const).forEach(period => {
      const people = period === 'AM' ? day.am : day.pm;
      const ratio = people / floor.capacity;
      if (people > 0 && (!peak || ratio > peak.ratio)) {
        peak = { floorLabel: floor.label, date: day.date, period, people, capacity: floor.capacity, ratio };
      }
    });
  }));

  return {
    averageOccupancy: totalPeopleDays / (totalCapacity * report.dates.length),
    busiestWeekday: byRatio.length > 1 ? byRatio[0] : undefined,
    quietestWeekday: byRatio.length > 1 ? byRatio[byRatio.length - 1] : undefined,
    peak,
  };
}

export function toCsvRows(report: OfficeHeatMap) {
  return report.floors.flatMap(floor => {
    const byDate = countsByDate(floor);
    return report.dates.map(date => {
      const day = byDate.get(date);
      const am = day?.am ?? 0;
      const pm = day?.pm ?? 0;
      const percent = (people: number) => floor.capacity > 0 ? Math.round(people / floor.capacity * 100) : '';
      return {
        'Floor': floor.label,
        'Date': moment(date).format('DD/MM/YYYY'),
        'Capacity': floor.capacity,
        'People AM': am,
        'People PM': pm,
        'Occupancy AM %': percent(am),
        'Occupancy PM %': percent(pm),
      };
    });
  });
}
